import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Compass,
  Users2,
  Rocket,
  LayoutDashboard,
  BookMarked,
  ArrowUpRight,
  Check,
  Sparkles,
} from "lucide-react";
import { SOLUTIONS } from "../lib/solutions";
import { SERVICE_HERO_IMAGES } from "../lib/content";
import { CALENDLY_URL } from "../lib/site";
import ServiceEnquiryForm from "../components/ServiceEnquiryForm";
import { Reveal } from "../lib/motion";

const ICONS = { Compass, Users2, Rocket, LayoutDashboard, BookMarked };

export default function SolutionDetail() {
  const { slug } = useParams();
  const service = SOLUTIONS.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/solutions" replace />;
  const Icon = ICONS[service.icon] || Compass;

  // Pick next/prev services for the pager at the bottom
  const idx = SOLUTIONS.findIndex((s) => s.slug === slug);
  const nextService = SOLUTIONS[(idx + 1) % SOLUTIONS.length];

  return (
    <div data-testid={`solution-detail-${slug}`}>
      {/* Hero */}
      <section
        className="relative border-b-2 border-[#1A1A1A] overflow-hidden"
        style={{ backgroundColor: service.color, color: service.text }}
      >
        {SERVICE_HERO_IMAGES[slug] && (
          <>
            <img
              src={SERVICE_HERO_IMAGES[slug]}
              alt={`${service.title} — TinkerShift in action`}
              className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-multiply pointer-events-none"
              data-testid={`detail-hero-image-${slug}`}
            />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: `linear-gradient(135deg, ${service.color}EE 0%, ${service.color}AA 60%, ${service.color}22 100%)`,
              }}
            />
          </>
        )}
        <div className="absolute inset-0 bg-dots opacity-20 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-24">
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/solutions"
              className="font-mono text-xs uppercase tracking-widest underline-offset-4 hover:underline"
            >
              ← All solutions
            </Link>
            {service.status && (
              <span className="inline-flex items-center gap-1 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold nb-shadow-sm">
                <Sparkles size={11} strokeWidth={2.5} /> {service.status}
              </span>
            )}
          </div>
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="h-14 w-14 rounded-2xl bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] flex items-center justify-center nb-shadow-sm">
                <Icon size={26} strokeWidth={2.5} />
              </span>
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] opacity-80">
                Solution {service.number} · {service.accent}
              </p>
              <h1 className="mt-3 font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tighter">
                {service.headline}
              </h1>
              <p className="mt-5 text-lg md:text-xl font-medium opacity-95 max-w-2xl leading-relaxed">
                {service.subheading}
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="rounded-3xl bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] p-6 nb-shadow">
                <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                  Indicative investment
                </p>
                <p className="mt-2 font-display font-black text-4xl md:text-5xl leading-none tracking-tighter">
                  From ${service.investment.from.toLocaleString("en-AU")}
                </p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                  AUD{service.investment.per ? ` / ${service.investment.per}` : ""}
                </p>
                <p className="mt-3 text-sm text-[#4A4A4A] font-medium">
                  {service.investment.note}
                </p>
                <a
                  href="#enquire"
                  data-testid={`detail-cta-top-${slug}`}
                  className="mt-5 inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-widest nb-shadow-sm nb-press"
                >
                  {service.cta} <ArrowUpRight size={14} strokeWidth={2.5} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="bg-white border-b-2 border-[#1A1A1A]">
        <div className="mx-auto max-w-5xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FF6600]">
              Overview
            </p>
            <p className="mt-4 text-xl md:text-2xl text-[#1A1A1A] font-medium leading-relaxed">
              {service.description}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Deliverables / Training areas / Features */}
      {service.deliverables && (
        <DetailList
          testId={`deliverables-${slug}`}
          eyebrow="What's included"
          title="Deliverables"
          items={service.deliverables}
          accent={service.color}
        />
      )}
      {service.training_areas && (
        <section
          data-testid={`training-areas-${slug}`}
          className="bg-[#F8F9FA] border-b-2 border-[#1A1A1A]"
        >
          <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FF007F]">
                Training areas
              </p>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
                Five hands-on technology streams.
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.training_areas.map((t, i) => (
                <motion.div
                  key={t.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="rounded-2xl bg-white border-2 border-[#1A1A1A] p-5 nb-shadow-sm"
                >
                  <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                    Stream 0{i + 1}
                  </p>
                  <h3 className="mt-1 font-display font-black text-xl tracking-tight">
                    {t.name}
                  </h3>
                  <p className="mt-2 text-sm text-[#4A4A4A] font-semibold">
                    {t.blurb}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      {service.additional_topics && (
        <DetailList
          testId={`additional-topics-${slug}`}
          eyebrow="Additional PD topics"
          title="We also cover…"
          items={service.additional_topics}
          accent={service.color}
          dark
        />
      )}
      {service.delivery_options && (
        <DetailList
          testId={`delivery-options-${slug}`}
          eyebrow="Delivery options"
          title="Flexible formats, built around you."
          items={service.delivery_options}
          accent={service.color}
        />
      )}
      {service.planned_features && (
        <DetailList
          testId={`planned-features-${slug}`}
          eyebrow="Planned features · In Development"
          title="What the platform will do."
          items={service.planned_features}
          accent={service.color}
          dark
        />
      )}
      {service.pathways && (
        <section
          data-testid={`pathways-${slug}`}
          className="bg-white border-b-2 border-[#1A1A1A]"
        >
          <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#00C2B8]">
                Five learning pathways
              </p>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
                Covering every creative tech discipline.
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-4">
              {service.pathways.map((p, i) => (
                <div
                  key={p}
                  className="rounded-2xl border-2 border-[#1A1A1A] p-5 nb-shadow-sm"
                  style={{
                    backgroundColor: ["#FFD700", "#FF007F", "#FF6600", "#0055FF", "#00C2B8"][i],
                    color: i === 0 || i === 4 ? "#1A1A1A" : "#FFFFFF",
                  }}
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest opacity-80">
                    Pathway 0{i + 1}
                  </p>
                  <p className="mt-1 font-display font-black text-lg leading-tight tracking-tight">
                    {p}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      {service.resource_categories && (
        <DetailList
          testId={`resource-categories-${slug}`}
          eyebrow="Resource categories"
          title="What you’ll get."
          items={service.resource_categories}
          accent={service.color}
        />
      )}
      {service.packages && (
        <section
          data-testid={`packages-${slug}`}
          className="bg-[#F8F9FA] border-b-2 border-[#1A1A1A]"
        >
          <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FF6600]">
                Potential packages
              </p>
              <h2 className="mt-4 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
                Pick a pack, start making.
              </h2>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.packages.map((p, i) => (
                <motion.div
                  key={p}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="rounded-2xl bg-white border-2 border-[#1A1A1A] p-6 nb-shadow-sm nb-lift"
                >
                  <p className="font-mono text-[11px] uppercase tracking-widest text-[#4A4A4A]">
                    Package 0{i + 1}
                  </p>
                  <h3 className="mt-1 font-display font-black text-xl md:text-2xl tracking-tight">
                    {p}
                  </h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      {service.add_ons && (
        <DetailList
          testId={`add-ons-${slug}`}
          eyebrow="Optional add-ons"
          title="Scale as you grow."
          items={service.add_ons}
          accent={service.color}
          dark
        />
      )}

      {/* Ideal for */}
      {service.ideal_for && (
        <section className="bg-[#1A1A1A] text-white border-b-2 border-[#1A1A1A]">
          <div className="mx-auto max-w-5xl px-5 md:px-10 lg:px-12 py-14">
            <Reveal>
              <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#FFD700]">
                Ideal for
              </p>
              <p className="mt-3 font-display font-black text-2xl md:text-4xl leading-tight tracking-tight">
                {service.ideal_for}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Enquiry */}
      <section
        id="enquire"
        data-testid={`enquiry-section-${slug}`}
        className="bg-[#F8F9FA] border-b-2 border-[#1A1A1A] relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="font-mono text-xs tracking-[0.25em] uppercase text-[#0055FF]">
              Enquire
            </p>
            <h2 className="mt-4 font-display font-black text-4xl md:text-5xl leading-[1.02] tracking-tighter">
              Ready to talk{" "}
              <span className="bg-[#FFD700] px-2 inline-block">
                {service.accent}
              </span>
              ?
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#4A4A4A] font-medium leading-relaxed">
              Share a bit about your organisation and goals — we’ll come back
              within 1–2 business days with a tailored next step.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`detail-calendly-${slug}`}
                className="inline-flex items-center gap-2 bg-[#1A1A1A] text-white border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-widest nb-shadow-sm nb-press"
              >
                …or book a call <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <ServiceEnquiryForm
              service={service.slug}
              serviceLabel={service.title}
              sourcePage={`/solutions/${service.slug}`}
              accent={service.color === "#FFD700" ? "#1A1A1A" : service.color}
              ctaLabel={service.cta}
            />
          </div>
        </div>
      </section>

      {/* Next solution */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-10 flex items-center justify-between gap-6 flex-wrap">
          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-widest nb-shadow-sm nb-press"
          >
            ← All solutions
          </Link>
          <Link
            to={`/solutions/${nextService.slug}`}
            data-testid={`detail-next-${slug}`}
            className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1A1A1A] border-2 border-[#1A1A1A] rounded-full px-5 py-2.5 font-bold uppercase text-xs tracking-widest nb-shadow-sm nb-press"
          >
            Next · {nextService.title}{" "}
            <ArrowUpRight size={14} strokeWidth={2.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function DetailList({ eyebrow, title, items, accent, dark, testId }) {
  return (
    <section
      data-testid={testId}
      className={`${dark ? "bg-[#1A1A1A] text-white" : "bg-white"} border-b-2 border-[#1A1A1A]`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-16 md:py-20">
        <Reveal>
          <p
            className="font-mono text-xs tracking-[0.25em] uppercase"
            style={{ color: dark ? "#FFD700" : "#FF007F" }}
          >
            {eyebrow}
          </p>
          <h2 className="mt-4 font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter">
            {title}
          </h2>
        </Reveal>
        <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item, i) => (
            <motion.li
              key={item}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              className={`flex items-start gap-3 rounded-2xl border-2 ${
                dark
                  ? "border-white/20 bg-white/5"
                  : "border-[#1A1A1A] bg-[#F8F9FA]"
              } p-4`}
            >
              <span
                className="mt-0.5 inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border-2 border-[#1A1A1A]"
                style={{ backgroundColor: accent }}
              >
                <Check size={14} strokeWidth={3} />
              </span>
              <span className="font-semibold text-sm md:text-base">{item}</span>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
